"use client";

// Libraries
import { useForm } from "react-hook-form";
import { useState } from "react";
import { Link, useRouter } from "@/i18n/navigation";
import Logo from "@/components/ui/Logo/Logo";

// Components
import Button from "@/components/ui/Button/Button";
import { toast } from "react-toastify";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";

export default function ForgottenPasswordComponent({ session, ...props }) {
    // Variables
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();
    const {
        register: register2,
        handleSubmit: handleSubmit2,
        formState: { errors: errors2 },
    } = useForm();
    const router = useRouter();
    const searchParams = useSearchParams();
    const key = searchParams.get("key");
    const t = useTranslations();

    // States
    const [isLoading, setIsLoading] = useState(false);
    const [emailSended, setEmailSended] = useState(false);

    // Redirect on "/" if user is already logged in
    if (session) {
        router.push("/");
        return "";
    }

    // Methods
    const onSubmit = async (data) => {
        if (!isLoading) {
            setIsLoading(true);
            const response = await fetch("/api/security/forgotten-password", {
                method: "post",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });

            const fetchedData = await response.json();

            if (!response.ok) {
                setIsLoading(false);
                toast.error(fetchedData.message);
            } else {
                setIsLoading(false);
                setEmailSended(true);
            }
            setIsLoading(false);
        }
    };
    const onSubmitReset = async (data) => {
        // Check if password and passwordConfirm are identical
        if (data.password != data.passwordConfirm) {
            toast.error(t("forgottenpassword_notSamePasswords"));
            return;
        }

        if (!isLoading) {
            setIsLoading(true);

            const response = await fetch(
                "/api/security/forgotten-password/confirm",
                {
                    method: "post",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ ...data, key: key }),
                },
            );

            const fetchedData = await response.json();

            if (!response.ok) {
                setIsLoading(false);
                toast.error(fetchedData.message);
            } else {
                setIsLoading(false);
                setEmailSended(true);
            }
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center h-screen bg-blue-50 p-5">
            <section className="md:w-[450px] w-full rounded-xl border p-10 background-blur-white">
                <Logo />
                {key ? (
                    emailSended ? (
                        <>
                            {/* Title */}
                            <h1 className="font-sourceSans text-3xl dark:text-white mt-4 font-bold md:text-left text-center">
                                {t("forgottenPassword_passwordChanged")}
                            </h1>

                            <p className="mt-1 text-gray-500">
                                {t(
                                    "forgottenPassword_passwordChangedDescription",
                                )}
                            </p>
                            <Link href="/login">
                                <Button block>
                                    {t("forgottenPassword_login")}
                                </Button>
                            </Link>
                        </>
                    ) : (
                        <>
                            {/* Title */}
                            <h1 className="font-sourceSans text-3xl dark:text-white mt-4 font-bold md:text-left text-center">
                                {t("forgottenPassword_title")}
                            </h1>

                            {/* Form */}
                            <form onSubmit={handleSubmit2(onSubmitReset)}>
                                <p className="mt-1 text-gray-500">
                                    {t("forgottenPassword_description")}
                                </p>
                                <p>
                                    <label htmlFor="password" className="label">
                                        {t("forgottenPassword_newPassword")}
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        placeholder={t(
                                            "forgottenPassword_newPassword",
                                        )}
                                        className="input"
                                        {...register2("password", {
                                            required: true,
                                        })}
                                    />
                                    {errors.password &&
                                        errors.password.type === "required" && (
                                            <small className="text-red-500">
                                                {t(
                                                    "forgottenPassword_required",
                                                )}
                                            </small>
                                        )}
                                </p>
                                <p>
                                    <label
                                        htmlFor="passwordConfirm"
                                        className="label"
                                    >
                                        {t("forgottenPassword_confirmPassword")}
                                    </label>
                                    <input
                                        type="password"
                                        id="passwordConfirm"
                                        placeholder={t(
                                            "forgottenPassword_confirmPassword",
                                        )}
                                        className="input"
                                        {...register2("passwordConfirm", {
                                            required: true,
                                        })}
                                    />
                                    {errors.passwordConfirm &&
                                        errors.passwordConfirm.type ===
                                            "required" && (
                                            <small className="text-red-500">
                                                {t(
                                                    "forgottenPassword_required",
                                                )}
                                            </small>
                                        )}
                                </p>
                                <div className="flex justify-end mt-5">
                                    <Button isLoading={isLoading} progress>
                                        {t("forgottenPassword_reset")}
                                    </Button>
                                </div>
                            </form>
                        </>
                    )
                ) : !emailSended ? (
                    <>
                        {/* Title */}
                        <h1 className="font-sourceSans text-3xl dark:text-white mt-4 font-bold md:text-left text-center">
                            {t("forgottenPassword_new")}
                        </h1>

                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="mt-1 text-gray-500"
                        >
                            <p>{t("forgottenPassword_newDescription")}</p>
                            <p>
                                <label htmlFor="email" className="label">
                                    {t("forgottenPassword_email")}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder={t("forgottenPassword_email")}
                                    className="input"
                                    {...register("email", {
                                        required: true,
                                        pattern:
                                            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    })}
                                />
                                {errors.email &&
                                    errors.email.type === "required" && (
                                        <small className="text-red-500">
                                            {t(
                                                "forgottenPassword_emailRequired",
                                            )}
                                        </small>
                                    )}
                                {errors.email &&
                                    errors.email.type === "pattern" && (
                                        <small className="text-red-500">
                                            {t(
                                                "forgottenPassword_emailInvalid",
                                            )}
                                        </small>
                                    )}
                            </p>
                            <div className="flex justify-end mt-5">
                                <Button isLoading={isLoading} progress>
                                    {t("forgottenPassword_submit")}
                                </Button>
                            </div>
                        </form>
                        <div className="mt-5">
                            <Link
                                href="/login"
                                className="text-gray-500 text-sm"
                            >
                                {t("forgottenPassword_redirectForLogin")}
                            </Link>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Title */}
                        <h1 className="font-sourceSans text-3xl dark:text-white mt-4 font-bold md:text-left text-center">
                            {t("forgottenPassword_emailSent")}
                        </h1>
                        <p className="mt-1 text-gray-500">
                            {t("forgottenPassword_emailSentDescription")}
                        </p>
                    </>
                )}
            </section>
        </div>
    );
}
